import { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Header, BottomNav } from "../components/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Switch } from "../components/ui/switch";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { format, addDays } from "date-fns";
import { es } from "date-fns/locale";
import { ArrowLeft, ImagePlus, Gavel, ShoppingCart, CalendarIcon, Clock, Upload, Loader2, X, Trash2, Save, Percent, Recycle, AlertTriangle } from "lucide-react";
import { ProductImageGallery } from "../components/ProductImageGallery";
import { ImageLibraryPicker } from "../components/ImageLibraryPicker";
import { CATEGORIES, normalizeCategory } from "../lib/categories";

const CONDITION_OPTIONS = ["Nuevo", "Usado - Excelente", "Usado - Bueno", "Para piezas"];

const EditProductPage = () => {
  const navigate = useNavigate();
  const { productId } = useParams();
  const { token, user } = useAuth();
  const fileInputRef = useRef(null);
  const additionalFileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [category, setCategory] = useState("otros");
  const [product, setProduct] = useState(null);
  
  // Form states
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [additionalImages, setAdditionalImages] = useState([]);
  const [saleType, setSaleType] = useState("direct");
  const [price, setPrice] = useState("");
  const [buyNowPrice, setBuyNowPrice] = useState("");
  const [auctionEndDate, setAuctionEndDate] = useState(null);
  const [auctionEndTime, setAuctionEndTime] = useState("12:00");
  
  // Discount states
  const [hasDiscount, setHasDiscount] = useState(false);
  const [discountCode, setDiscountCode] = useState("");
  const [discountPercent, setDiscountPercent] = useState("");

  // Offer negotiation settings (direct sale only)
  const [acceptsOffers, setAcceptsOffers] = useState(true);
  const [autoAcceptOfferPrice, setAutoAcceptOfferPrice] = useState("");

  // Ficha técnica (optional)
  const [conditionGrade, setConditionGrade] = useState("");
  const [storageRam, setStorageRam] = useState("");
  const [includedAccessories, setIncludedAccessories] = useState("");
  const [warrantyInfo, setWarrantyInfo] = useState("");

  // Fetch product data
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/products/${productId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true
        });
        const p = response.data;
        
        // Check ownership
        if (p.user_id !== user?.user_id && !user?.is_admin) {
          toast.error("No tienes permiso para editar este producto");
          navigate(-1);
          return;
        }
        
        setProduct(p);
        setTitle(p.title || "");
        setDescription(p.description || "");
        setImageUrl(p.image_url || "");
        setAdditionalImages(p.images || []);
        setSaleType(p.sale_type || "direct");
        setCategory(normalizeCategory(p.category));
        setPrice(p.price?.toString() || "");
        setBuyNowPrice(p.buy_now_price?.toString() || "");
        setHasDiscount(p.has_discount || false);
        setDiscountCode(p.discount_code || "");
        setDiscountPercent(p.discount_percent?.toString() || "");
        setAcceptsOffers(p.accepts_offers !== undefined ? p.accepts_offers : true);
        setAutoAcceptOfferPrice(p.auto_accept_offer_price?.toString() || "");
        setConditionGrade(p.condition_grade || "");
        setStorageRam(p.storage_ram || "");
        setIncludedAccessories(p.included_accessories || "");
        setWarrantyInfo(p.warranty_info || "");
        
        if (p.auction_end_time) {
          const endDate = new Date(p.auction_end_time);
          setAuctionEndDate(endDate);
          setAuctionEndTime(format(endDate, "HH:mm"));
        }
      } catch (error) {
        toast.error("Error al cargar el producto");
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };
    
    if (productId && user) {
      fetchProduct();
    }
  }, [productId, token, user, navigate]);

  const handleFileUpload = async (e, isAdditional = false) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`"${file.name}": Solo JPG, PNG, WebP o GIF`);
        e.target.value = "";
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`"${file.name}": no puede superar 5MB`);
        e.target.value = "";
        return;
      }
    }

    // Routing: main button → first becomes main, rest go to additional
    //         additional button → all go to additional
    const mainFile = isAdditional ? null : files[0];
    const additionalFiles = isAdditional ? files : files.slice(1);

    if (additionalImages.length + additionalFiles.length > 9) {
      const available = Math.max(0, 9 - additionalImages.length);
      toast.error(`Máximo 10 imágenes. Puedes agregar hasta ${available} adicionales.`);
      e.target.value = "";
      return;
    }

    setUploading(true);
    let successCount = 0;
    try {
      if (mainFile) {
        const formData = new FormData();
        formData.append("file", mainFile);
        const response = await axios.post(`${API}/upload/image`, formData, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
          withCredentials: true
        });
        setImageUrl(response.data.url);
        successCount++;
      }
      for (const file of additionalFiles) {
        const formData = new FormData();
        formData.append("file", file);
        const response = await axios.post(`${API}/upload/image`, formData, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
          withCredentials: true
        });
        setAdditionalImages(prev => [...prev, response.data.url]);
        successCount++;
      }
      toast.success(`${successCount} ${successCount === 1 ? "imagen subida" : "imágenes subidas"} exitosamente`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al subir imagen");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeAdditionalImage = (index) => {
    setAdditionalImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleRecycleSelect = (urls) => {
    let main = imageUrl;
    const extra = [...additionalImages];
    let added = 0;
    for (const url of urls) {
      if (url === main || extra.includes(url)) continue;
      if (!main) { main = url; added++; }
      else if (extra.length < 9) { extra.push(url); added++; }
    }
    setImageUrl(main);
    setAdditionalImages(extra);
    setShowLibrary(false);
    if (added > 0) toast.success(`${added} foto${added !== 1 ? "s" : ""} reciclada${added !== 1 ? "s" : ""} de tu galería`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim() || !description.trim() || !imageUrl || !price) {
      toast.error("Por favor completa todos los campos requeridos");
      return;
    }

    if (saleType === "auction" && !auctionEndDate) {
      toast.error("Por favor selecciona la fecha de fin de subasta");
      return;
    }

    if (product && saleType !== product.sale_type) {
      const confirmed = window.confirm(
        "Cambiar el tipo de venta reiniciará el anuncio: se eliminarán las pujas/ofertas actuales y volverá a estar activo y sin vender. ¿Continuar?"
      );
      if (!confirmed) return;
    }

    setSaving(true);
    try {
      let auctionEnd = null;
      if (saleType === "auction" && auctionEndDate) {
        const [hours, minutes] = auctionEndTime.split(":").map(Number);
        auctionEnd = new Date(auctionEndDate);
        auctionEnd.setHours(hours, minutes, 0, 0);
      }

      const updateData = {
        title,
        description,
        image_url: imageUrl,
        images: additionalImages,
        sale_type: saleType,
        category: category,
        price: parseFloat(price),
        buy_now_price: saleType === "auction" && buyNowPrice ? parseFloat(buyNowPrice) : parseFloat(price),
        auction_end_time: auctionEnd?.toISOString() || null,
        discount_code: hasDiscount && discountCode ? discountCode : null,
        discount_percent: hasDiscount && discountPercent ? parseFloat(discountPercent) : null,
        accepts_offers: saleType === "direct" ? acceptsOffers : false,
        auto_accept_offer_price: (saleType === "direct" && acceptsOffers && autoAcceptOfferPrice)
          ? parseFloat(autoAcceptOfferPrice)
          : null,
        condition_grade: conditionGrade || null,
        storage_ram: storageRam || null,
        included_accessories: includedAccessories || null,
        warranty_info: warrantyInfo || null
      };

      await axios.put(`${API}/products/${productId}`, updateData, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });

      toast.success("¡Producto actualizado exitosamente!");
      navigate(`/product/${productId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar producto");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <Header />
        <div className="flex justify-center items-center py-20">
          <Loader2 className="animate-spin" size={32} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9] pb-20 md:pb-8">
      <Header />

      <main className="max-w-lg mx-auto px-4 py-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#64748B] hover:text-[#0F172A] mb-6"
          data-testid="back-button"
        >
          <ArrowLeft size={20} />
          Volver
        </button>

        <h1 className="text-2xl font-bold text-[#0F172A] mb-6">Editar Producto</h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Main Image */}
          <div className="space-y-2">
            <Label>Imagen principal *</Label>
            {imageUrl ? (
              <div className="relative rounded-2xl overflow-hidden">
                <img src={imageUrl} alt="Preview" className="w-full aspect-square object-cover" />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => handleFileUpload(e, false)}
                />
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="absolute bottom-4 right-4 rounded-full"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? <Loader2 className="animate-spin" size={16} /> : "Cambiar / Agregar más"}
                </Button>
              </div>
            ) : (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => handleFileUpload(e, false)}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full aspect-square rounded-2xl border-2 border-dashed border-[#E2E8F0] bg-white flex flex-col items-center justify-center gap-3 hover:border-[#475569] transition-colors"
                  disabled={uploading}
                  data-testid="upload-main-image-btn"
                >
                  {uploading ? (
                    <Loader2 className="animate-spin" size={48} />
                  ) : (
                    <>
                      <ImagePlus size={48} className="text-[#64748B]" />
                      <span className="text-[#64748B]">Agregar imagen(es)</span>
                      <span className="text-xs text-[#94A3B8]">La primera será la principal</span>
                    </>
                  )}
                </button>
              </>
            )}
          </div>

          {/* Unified gallery (main + additional) with drag&drop reordering */}
          {imageUrl && (
            <div className="space-y-2">
              <Label>Galería de imágenes ({(additionalImages.length || 0) + 1}/10)</Label>
              <ProductImageGallery
                images={[imageUrl, ...additionalImages]}
                onReorder={(next) => {
                  setImageUrl(next[0] || "");
                  setAdditionalImages(next.slice(1));
                }}
                onRemove={(idx) => {
                  const all = [imageUrl, ...additionalImages];
                  all.splice(idx, 1);
                  setImageUrl(all[0] || "");
                  setAdditionalImages(all.slice(1));
                }}
              />
              {additionalImages.length < 9 && (
                <>
                  <input
                    ref={additionalFileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => handleFileUpload(e, true)}
                  />
                  <button
                    type="button"
                    onClick={() => additionalFileInputRef.current?.click()}
                    className="w-full py-2.5 rounded-lg border-2 border-dashed border-[#E2E8F0] flex items-center justify-center gap-2 hover:border-[#475569] transition-colors text-sm text-[#64748B]"
                    disabled={uploading}
                    data-testid="upload-additional-image-btn"
                  >
                    {uploading ? (
                      <><Loader2 className="animate-spin" size={16} /> Subiendo...</>
                    ) : (
                      <><ImagePlus size={16} /> Agregar más imágenes</>
                    )}
                  </button>
                </>
              )}
            </div>
          )}

          {/* Recycle previous photos */}
          <button
            type="button"
            onClick={() => setShowLibrary(true)}
            className="w-full py-2.5 rounded-lg border-2 border-dashed border-emerald-300 flex items-center justify-center gap-2 text-emerald-700 hover:border-emerald-500 hover:bg-emerald-50 transition-colors text-sm"
            data-testid="recycle-images-btn"
          >
            <Recycle size={16} /> Reciclar fotos anteriores
          </button>
          <ImageLibraryPicker
            open={showLibrary}
            onOpenChange={setShowLibrary}
            onSelect={handleRecycleSelect}
            maxSelectable={10 - ((imageUrl ? 1 : 0) + additionalImages.length)}
            excludeUrls={[imageUrl, ...additionalImages].filter(Boolean)}
          />

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Título *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ej: iPhone 13 Pro Max 256GB"
              data-testid="product-title-input"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Descripción *</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe el producto, estado, accesorios incluidos..."
              rows={4}
              data-testid="product-description-input"
            />
          </div>

          {/* Category */}
          <div className="space-y-2">
            <Label>Categoría</Label>
            <div className="flex flex-wrap gap-2" data-testid="category-selector">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setCategory(cat.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    category === cat.id
                      ? "bg-[#475569] text-white border-[#475569]"
                      : "bg-white text-[#64748B] border-[#E2E8F0] hover:border-[#475569]"
                  }`}
                  data-testid={`category-${cat.id}`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          {/* Ficha técnica (optional) */}
          <div className="space-y-4 p-4 bg-slate-50 border border-slate-200 rounded-xl">
            <Label className="text-[#0F172A] font-semibold">Ficha técnica (opcional)</Label>
            <div className="space-y-2">
              <Label className="text-sm">Condición / Grado</Label>
              <div className="flex flex-wrap gap-2" data-testid="condition-grade-selector">
                {CONDITION_OPTIONS.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setConditionGrade(conditionGrade === opt ? "" : opt)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      conditionGrade === opt
                        ? "bg-[#475569] text-white border-[#475569]"
                        : "bg-white text-[#64748B] border-[#E2E8F0] hover:border-[#475569]"
                    }`}
                    data-testid={`condition-grade-${opt.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="storageRam" className="text-sm">Almacenamiento y RAM</Label>
              <Input
                id="storageRam"
                value={storageRam}
                onChange={(e) => setStorageRam(e.target.value)}
                placeholder="Ej: 256GB / 8GB RAM"
                data-testid="storage-ram-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="includedAccessories" className="text-sm">Accesorios incluidos</Label>
              <Input
                id="includedAccessories"
                value={includedAccessories}
                onChange={(e) => setIncludedAccessories(e.target.value)}
                placeholder="Ej: Cargador, caja original"
                data-testid="included-accessories-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="warrantyInfo" className="text-sm">Garantía</Label>
              <Input
                id="warrantyInfo"
                value={warrantyInfo}
                onChange={(e) => setWarrantyInfo(e.target.value)}
                placeholder="Ej: 30 días de garantía Clueer"
                data-testid="warranty-info-input"
              />
            </div>
          </div>

          {/* Sale Type */}
          <div className="space-y-3">
            <Label>Tipo de venta</Label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setSaleType("direct")}
                className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                  saleType === "direct"
                    ? "border-[#10B981] bg-[#10B981]/5"
                    : "border-[#E2E8F0] hover:border-[#10B981]/50"
                }`}
                data-testid="sale-type-direct"
              >
                <ShoppingCart size={24} className={saleType === "direct" ? "text-[#10B981]" : "text-[#64748B]"} />
                <span className={`font-medium ${saleType === "direct" ? "text-[#10B981]" : "text-[#64748B]"}`}>
                  Mejor Oferta
                </span>
              </button>
              <button
                type="button"
                onClick={() => setSaleType("auction")}
                className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                  saleType === "auction"
                    ? "border-[#3B82F6] bg-[#3B82F6]/5"
                    : "border-[#E2E8F0] hover:border-[#3B82F6]/50"
                }`}
                data-testid="sale-type-auction"
              >
                <Gavel size={24} className={saleType === "auction" ? "text-[#3B82F6]" : "text-[#64748B]"} />
                <span className={`font-medium ${saleType === "auction" ? "text-[#3B82F6]" : "text-[#64748B]"}`}>
                  Subasta
                </span>
              </button>
            </div>
            {product && saleType !== product.sale_type && (
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="sale-type-change-warning">
                <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800">
                  Al cambiar el tipo de venta, el anuncio se reiniciará: se eliminarán las pujas/ofertas actuales y volverá a estar activo y sin vender.
                </p>
              </div>
            )}
          </div>

          {/* Price */}
          <div className="space-y-2">
            <Label htmlFor="price">{saleType === "auction" ? "Precio inicial *" : "Precio *"}</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]">$</span>
              <Input
                id="price"
                type="number"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                className="pl-7"
                placeholder="0"
                data-testid="product-price-input"
              />
            </div>
          </div>

          {/* Buy Now Price (only for auctions) */}
          {saleType === "auction" && (
            <div className="space-y-2">
              <Label htmlFor="buyNowPrice">Precio "Compra sin Subasta" (opcional)</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]">$</span>
                <Input
                  id="buyNowPrice"
                  type="number"
                  value={buyNowPrice}
                  onChange={(e) => setBuyNowPrice(e.target.value)}
                  className="pl-7"
                  placeholder="0"
                  data-testid="product-buynow-input"
                />
              </div>
              <p className="text-xs text-[#64748B]">Precio para compra inmediata sin participar en subasta</p>
            </div>
          )}

          {/* Auction End Date */}
          {saleType === "auction" && (
            <div className="space-y-2">
              <Label>Fecha y hora de fin de subasta *</Label>
              <div className="flex gap-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1 justify-start text-left font-normal"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {auctionEndDate ? format(auctionEndDate, "PPP", { locale: es }) : "Seleccionar fecha"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={auctionEndDate}
                      onSelect={setAuctionEndDate}
                      disabled={(date) => date < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#64748B]" />
                  <Input
                    type="time"
                    value={auctionEndTime}
                    onChange={(e) => setAuctionEndTime(e.target.value)}
                    className="pl-10 w-32"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Offers / Fixed Price toggle - only for direct sales */}
          {saleType === "direct" && (
            <div className="space-y-3 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1">
                  <Label className="text-emerald-900 font-semibold">¿Aceptar ofertas?</Label>
                  <p className="text-xs text-emerald-700 mt-0.5">
                    Si desactivás, el producto se vende solo al precio fijo (no aparece "Último precio?").
                  </p>
                </div>
                <Switch
                  checked={acceptsOffers}
                  onCheckedChange={setAcceptsOffers}
                  data-testid="accepts-offers-toggle"
                />
              </div>

              {acceptsOffers && (
                <div className="space-y-2 pt-2 border-t border-emerald-200">
                  <Label className="text-emerald-900 text-sm">Precio mínimo de auto-aceptación (opcional)</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-600 font-semibold">$</span>
                    <Input
                      type="number"
                      placeholder="Ej: 180"
                      value={autoAcceptOfferPrice}
                      onChange={(e) => setAutoAcceptOfferPrice(e.target.value)}
                      className="pl-7 rounded-xl border-emerald-200"
                      min="0"
                      step="0.01"
                      data-testid="auto-accept-price-input"
                    />
                  </div>
                  <p className="text-xs text-emerald-700">
                    Toda oferta ≥ este monto será aceptada automáticamente. Dejalo vacío para revisar manualmente.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Discount Section */}
          <div className="space-y-4 p-4 bg-purple-50 rounded-xl border border-purple-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Percent size={18} className="text-purple-600" />
                <Label className="text-purple-800 font-medium">Código de descuento</Label>
              </div>
              <Switch
                checked={hasDiscount}
                onCheckedChange={setHasDiscount}
                data-testid="discount-toggle"
              />
            </div>
            
            {hasDiscount && (
              <div className="space-y-3 pt-2">
                <div>
                  <Label htmlFor="discountCode" className="text-sm">Código</Label>
                  <Input
                    id="discountCode"
                    value={discountCode}
                    onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                    placeholder="Ej: DESCUENTO10"
                    className="mt-1"
                    data-testid="discount-code-input"
                  />
                </div>
                <div>
                  <Label htmlFor="discountPercent" className="text-sm">Porcentaje de descuento</Label>
                  <div className="relative mt-1">
                    <Input
                      id="discountPercent"
                      type="number"
                      min="1"
                      max="99"
                      value={discountPercent}
                      onChange={(e) => setDiscountPercent(e.target.value)}
                      placeholder="10"
                      className="pr-8"
                      data-testid="discount-percent-input"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B]">%</span>
                  </div>
                </div>
                {discountPercent && price && (
                  <p className="text-xs text-purple-600">
                    Precio con descuento: ${(parseFloat(price) * (1 - parseFloat(discountPercent) / 100)).toFixed(2)}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={saving || uploading}
            className="w-full rounded-full h-12 bg-[#10B981] hover:bg-[#059669]"
            data-testid="save-product-btn"
          >
            {saving ? (
              <Loader2 className="animate-spin" size={20} />
            ) : (
              <>
                <Save size={18} className="mr-2" />
                Guardar Cambios
              </>
            )}
          </Button>
        </form>
      </main>

      <BottomNav />
    </div>
  );
};

export default EditProductPage;
